// src/components/home/RecentlyViewedCars.jsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { shortlistService } from "../../services/shortlist";
import { useAuth } from "../../context/AuthContext";
import "../../styles/home/RecentlyViewedCars.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getImageUrl(car) {
  const raw = car?.images_urls?.[0] || car?.image_url || null;
  if (!raw) return null;
  if (raw.startsWith("http")) return raw;
  return `${API_BASE}${raw}`;
}

function formatPrice(price) {
  if (!price) return "Price on request";
  const lakh = price / 100000;
  return `₹${lakh % 1 === 0 ? lakh.toFixed(0) : lakh.toFixed(2)} Lakh`;
}

function formatKm(km) {
  if (!km) return null;
  return `${Number(km).toLocaleString("en-IN")} km`;
}

// ── localStorage helpers ──────────────────────────────────────────────────────

const RV_KEY = "rv_cars";
const MAX_RV = 10;

export function recordCarView(car) {
  try {
    const stored = JSON.parse(localStorage.getItem(RV_KEY) || "[]");
    const filtered = stored.filter((c) => String(c.id) !== String(car.id));
    const slim = {
      id: car.id,
      title: car.title,
      brand: car.brand,
      model: car.model,
      year: car.year,
      price: car.price,
      location: car.location,
      fuel_type: car.fuel_type,
      transmission: car.transmission,
      km_driven: car.km_driven,
      images_urls: car.images_urls,
      shortlist_count: car.shortlist_count,
      viewedAt: Date.now(),
    };
    localStorage.setItem(
      RV_KEY,
      JSON.stringify([slim, ...filtered].slice(0, MAX_RV))
    );
    window.dispatchEvent(new Event("rv_updated"));
  } catch (_) {}
}

function getRecentlyViewed() {
  try {
    return JSON.parse(localStorage.getItem(RV_KEY) || "[]");
  } catch (_) {
    return [];
  }
}

// ── Car Card ──────────────────────────────────────────────────────────────────

function CarCard({ car, isShortlisted, shortlistCount, onToggleShortlist }) {
  const navigate = useNavigate();
  const imgUrl = getImageUrl(car);

  return (
    <div className="rvc-card" onClick={() => navigate(`/car/${car.id}`)}>
      <div className="rvc-img-wrap">
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={car.title || `${car.brand} ${car.model}`}
            className="rvc-img"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = "none";
              if (e.target.nextElementSibling)
                e.target.nextElementSibling.style.display = "flex";
            }}
          />
        ) : null}
        <div
          className="rvc-img-placeholder"
          style={{ display: imgUrl ? "none" : "flex" }}
        >
          <span>🚗</span>
        </div>
      </div>

      <div className="rvc-body">
        <div className="rvc-title-row">
          <h4 className="rvc-title">
            {car.title || `${car.year} ${car.brand} ${car.model}`}
          </h4>
          <button
            className={`rvc-heart${isShortlisted ? " rvc-heart--active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleShortlist(car.id);
            }}
            aria-label={isShortlisted ? "Remove from shortlist" : "Add to shortlist"}
          >
            <svg
              viewBox="0 0 24 24"
              fill={isShortlisted ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </button>
        </div>

        <div className="rvc-specs">
          {car.km_driven && <span>{formatKm(car.km_driven)}</span>}
          {car.km_driven && car.fuel_type && <span className="rvc-dot">·</span>}
          {car.fuel_type && (
            <span>
              {car.fuel_type.charAt(0).toUpperCase() + car.fuel_type.slice(1)}
            </span>
          )}
          {car.fuel_type && car.transmission && <span className="rvc-dot">·</span>}
          {car.transmission && (
            <span>
              {car.transmission.charAt(0).toUpperCase() + car.transmission.slice(1)}
            </span>
          )}
        </div>

        <div className="rvc-footer">
          <span className="rvc-price">{formatPrice(car.price)}</span>
          {shortlistCount > 0 && (
            <span className="rvc-shortlist-count">
              <svg viewBox="0 0 24 24" fill="#ef4444" width="11" height="11">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              {shortlistCount} shortlisted this car
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function RecentlyViewedCars() {
  const [cars, setCars] = useState([]);
  const [shortlistedIds, setShortlistedIds] = useState(new Set());
  const [shortlistCounts, setShortlistCounts] = useState({});
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // ✅ THE FIX: ref always mirrors current shortlistedIds so the toggle
  // callback never captures a stale closure and never reverts the heart.
  const shortlistedIdsRef = useRef(new Set());
  useEffect(() => {
    shortlistedIdsRef.current = shortlistedIds;
  }, [shortlistedIds]);

  // Load recently viewed cars from localStorage
  useEffect(() => {
    const rv = getRecentlyViewed();
    setCars(rv);
    const counts = {};
    rv.forEach((c) => {
      if (c.shortlist_count != null) counts[String(c.id)] = Number(c.shortlist_count);
    });
    setShortlistCounts(counts);
  }, []);

  // Re-sync when another tab or the detail page updates localStorage
  useEffect(() => {
    const sync = () => {
      const rv = getRecentlyViewed();
      setCars(rv);
    };
    window.addEventListener("storage", sync);
    window.addEventListener("rv_updated", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("rv_updated", sync);
    };
  }, []);

  // Fetch live shortlist counts per car from the backend
  useEffect(() => {
    if (!cars.length) return;
    const token = localStorage.getItem("authToken");
    cars.forEach((car) => {
      fetch(`${API_BASE}/api/v1/cars/${car.id}/shortlist-status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.shortlist_count != null) {
            setShortlistCounts((prev) => ({
              ...prev,
              [String(car.id)]: Number(data.shortlist_count),
            }));
          }
        })
        .catch(() => {});
    });
  }, [cars]);

  // Load which cars this user has shortlisted
  useEffect(() => {
    if (!user) {
      const empty = new Set();
      setShortlistedIds(empty);
      shortlistedIdsRef.current = empty;
      return;
    }
    shortlistService.getShortlistedCarIds()
      .then((ids) => {
        const s = new Set(ids.map(String));
        setShortlistedIds(s);
        shortlistedIdsRef.current = s;
      })
      .catch(() => {});
  }, [user]);

  // Scroll state tracker
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const t = setTimeout(updateScrollState, 120);
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      clearTimeout(t);
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [cars, updateScrollState]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector(".rvc-card");
    const amount = card ? card.offsetWidth + 16 : 280;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  // ✅ Same ref-based pattern as FeaturedCars — no stale closure, no reset.
  const handleToggleShortlist = useCallback(async (carId) => {
    if (!user) { navigate("/login"); return; }

    const id = String(carId);
    const wasShortlisted = shortlistedIdsRef.current.has(id);

    // Optimistic update — UI responds instantly
    setShortlistedIds((prev) => {
      const next = new Set(prev);
      wasShortlisted ? next.delete(id) : next.add(id);
      shortlistedIdsRef.current = next; // keep ref in sync
      return next;
    });
    setShortlistCounts((prev) => ({
      ...prev,
      [id]: Math.max(0, (prev[id] || 0) + (wasShortlisted ? -1 : 1)),
    }));

    try {
      if (wasShortlisted) {
        await shortlistService.removeFromShortlist(carId);
      } else {
        await shortlistService.addToShortlist(carId);
      }
    } catch (_) {
      // Revert on failure
      setShortlistedIds((prev) => {
        const next = new Set(prev);
        wasShortlisted ? next.add(id) : next.delete(id);
        shortlistedIdsRef.current = next;
        return next;
      });
      setShortlistCounts((prev) => ({
        ...prev,
        [id]: Math.max(0, (prev[id] || 0) + (wasShortlisted ? 1 : -1)),
      }));
    }
  }, [user, navigate]); // ✅ shortlistedIds intentionally excluded — read via ref

  if (!cars.length) return null;

  return (
    <section className="rvc-section">
      <h2 className="rvc-heading">Recently viewed cars</h2>

      <div className="rvc-outer">
        <button
          className={`rvc-arrow rvc-arrow--left${!canScrollLeft ? " rvc-arrow--disabled" : ""}`}
          onClick={() => scroll(-1)}
          disabled={!canScrollLeft}
          aria-label="Scroll left"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <div className="rvc-clip">
          <div className="rvc-track" ref={scrollRef}>
            {cars.map((car) => (
              <CarCard
                key={car.id}
                car={car}
                isShortlisted={shortlistedIds.has(String(car.id))}
                shortlistCount={shortlistCounts[String(car.id)] ?? 0}
                onToggleShortlist={handleToggleShortlist}
              />
            ))}
          </div>
        </div>

        <button
          className={`rvc-arrow rvc-arrow--right${!canScrollRight ? " rvc-arrow--disabled" : ""}`}
          onClick={() => scroll(1)}
          disabled={!canScrollRight}
          aria-label="Scroll right"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </section>
  );
}